import {CdkDragDrop, CdkDropList, CdkDropListGroup, moveItemInArray, transferArrayItem} from '@angular/cdk/drag-drop';
import {AsyncPipe} from '@angular/common';
import {ChangeDetectionStrategy, Component, EventEmitter, inject, OnDestroy, OnInit, Output} from '@angular/core';
import {FormControl} from '@angular/forms';
import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatTooltipModule} from '@angular/material/tooltip';
import {select, Store} from '@ngrx/store';
import * as _ from 'lodash';
import {BehaviorSubject, combineLatest, debounceTime, filter, map, Observable, of, Subscription, take} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {Project, ProjectAlert, ProjectStep, ProjectView} from 'src/app/core/model/project';
import {RewardCoin} from 'src/app/core/model/reward';
import {SkillModel} from 'src/app/core/model/skill';
import {ProjectTask, TaskTemplate} from 'src/app/core/model/task';
import {Tracker, TrackerMetric} from 'src/app/core/model/tracker';
import {selectLocationsMap} from 'src/app/core/store/locations/locations.selector';
import {
  AddProjectTaskAction,
  GetTaskTemplatesAction,
  MoveProjectTaskAction,
  PutProjectAction,
  UpdateAlertAction,
} from 'src/app/core/store/projects/projects.action';
import {
  selectProjectViews,
  selectProjectViewTokenFilterParams,
  selectProjectsMap,
  selectTaskTemplates,
} from 'src/app/core/store/projects/projects.selector';
import {selectCoinTypes} from 'src/app/core/store/rewards/rewards.selector';
import {selectSkillsMap} from 'src/app/core/store/skills/skills.selector';
import {selectTrackersMap} from 'src/app/core/store/trackers/trackers.selector';
import {Location} from 'src/app/locations/shared/model/location';
import {MessageService} from 'src/app/services/message.service';
import {AlertsDialogComponent} from 'src/app/shared/alerts/alerts-dialog/alerts-dialog.component';
import {OphAddButtonComponent} from 'src/app/shared/design/oph-add-button/oph-add-button.component';
import {
  OphButtonGroupComponent,
  OphButtonGroupOption,
} from 'src/app/shared/design/oph-button-group/oph-button-group.component';
import {OphChipButtonComponent} from 'src/app/shared/design/oph-chip-button/oph-chip-button.component';
import {OphIconModule} from 'src/app/shared/design/oph-icon/oph-icon.module';
import {OphSearchInputComponent} from 'src/app/shared/design/oph-inputs/oph-search-input/oph-search-input.component';
import {OphNewButtonComponent} from 'src/app/shared/design/oph-new-button/oph-new-button.component';
import {PipesModule} from 'src/app/shared/pipes/pipes.module';
import {ProjectsService} from 'src/app/shared/services/projects.service';
import {
  EDIT_PROJECT_TASK_DIALOG_SETTINGS,
  NEW_PROJECT_TASK_DIALOG_SETTINGS,
} from 'src/app/shared/tasks/projects-task-dialog/project-task-dialog-constants';
import {ProjectsTaskDialogComponent} from 'src/app/shared/tasks/projects-task-dialog/projects-task-dialog.component';
import {formatDuration} from 'src/app/shared/utils/duration.util';
import {TrackerMetricTypes} from 'src/app/trackers/shared/tracker-metric-types';
import {IsTrackerCompletePipe} from './shared/is-tracker-complete.pipe';
import {ProjectEditStepDialogComponent} from './step-dialog/project-edit-step-dialog.component';
import {ProjectsEditTaskComponent} from './task/projects-edit-task.component';
import {OphBackButtonComponent} from 'src/app/shared/design/oph-back-button/oph-back-button.component';
import {PROJECT_EDIT_ALERTS_DIALOG_CONFIG} from '../shared/projects-constants';
import {MobileService} from 'src/app/core/page/mobile.service';

interface TaskDropData {
  tasks?: ProjectTask[];
  taskTemplates?: ProjectTask[];
  isTemplate: boolean;
  stepIndex?: number;
  isStep?: boolean;
}

interface MetricDisplay {
  activation: {name: string; value: string};
  completion: {name: string; value: string};
}

@Component({
  selector: 'project-edit',
  standalone: true,
  imports: [
    ProjectsEditTaskComponent,
    AsyncPipe,
    OphButtonGroupComponent,
    OphSearchInputComponent,
    OphNewButtonComponent,
    OphIconModule,
    CdkDropListGroup,
    CdkDropList,
    OphAddButtonComponent,
    MatTooltipModule,
    MatIconModule,
    PipesModule,
    IsTrackerCompletePipe,
    OphChipButtonComponent,
    MatProgressSpinnerModule,
    OphBackButtonComponent,
  ],
  templateUrl: './project-edit.component.html',
  styleUrl: './project-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectEditComponent implements OnInit, OnDestroy {
  private readonly mobileService = inject(MobileService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  /** Project from route. Uses parent paramMap when loaded via router (edit child), or current paramMap when embedded in wrapper. */
  project$: Observable<Project | null> = (this.route.snapshot.paramMap.get('projectId')
    ? this.route.paramMap
    : this.route.parent?.paramMap ?? of({get: () => null} as unknown as ParamMap)
  ).pipe(
    switchMap((paramMap: ParamMap) =>
      this.store$.pipe(
        select(selectProjectsMap),
        map(projectsMap => {
          const id = paramMap.get('projectId');
          return id && projectsMap?.[id] ? projectsMap[id] : null;
        })
      )
    )
  );
  taskTemplates$: Observable<TaskTemplate[]> = this.store$.pipe(select(selectTaskTemplates));
  coinTypes$: Observable<RewardCoin[]> = this.store$.pipe(select(selectCoinTypes));

  requirementsMap$: Observable<Record<string, SkillModel>> = this.store$.pipe(select(selectSkillsMap));
  locationsMap$: Observable<Record<string, Location>> = this.store$.pipe(select(selectLocationsMap));
  trackersMap$: Observable<Record<string, Tracker>> = this.store$.pipe(select(selectTrackersMap));
  metricsDisplay$: Observable<MetricDisplay[]>;
  projectViews$: Observable<Record<string, ProjectView>> = this.store$.pipe(select(selectProjectViews));
  tokenParams$: Observable<Record<string, string>> = this.store$.pipe(select(selectProjectViewTokenFilterParams));

  selectedTab$ = new BehaviorSubject<string>('task templates');
  loadingStepIndex$ = new BehaviorSubject<number>(null);
  loadingTaskIndex$ = new BehaviorSubject<number>(null);
  loadingAlertIndex$ = new BehaviorSubject<{stepId: string; index: number}>(null);
  projectView$ = new BehaviorSubject<ProjectView | null>(null);
  readonly dialog = inject(MatDialog);
  tabOptions: OphButtonGroupOption[] = [{name: 'task templates'}, {name: 'milestones', disabled: true}];
  search: FormControl = new FormControl('');
  searchSub: Subscription;
  subscriptions = new Subscription();

  /** Emits when the user requests to leave edit view; parent should switch to challenge or project view. */
  @Output() exitEdit = new EventEmitter<void>();

  constructor(
    private store$: Store,
    private messageService: MessageService,
    private projectsService: ProjectsService
  ) {}

  ngOnInit() {
    this.store$.dispatch(new GetTaskTemplatesAction({search: ''}));

    this.searchSub = this.search.valueChanges.pipe(debounceTime(300)).subscribe(search => {
      this.store$.dispatch(new GetTaskTemplatesAction({search}));
    });

    // Initialize metricsDisplay$
    this.metricsDisplay$ = this.getMetricsDisplay();
  }

  ngOnDestroy() {
    this.searchSub.unsubscribe();
    this.subscriptions.unsubscribe();
    this.loadingAlertIndex$.next(null);
  }

  getMetricsDisplay(): Observable<MetricDisplay[]> {
    return combineLatest([this.project$, this.trackersMap$]).pipe(
      filter(([project, trackersMap]) => !!project && !!trackersMap),
      map(([project, trackersMap]) =>
        project.steps.map(step => {
          const activationTracker = trackersMap[step.activationTracker?.trackerId];
          const activationMetric = activationTracker?.metrics?.find(m => m._id === step.activationTracker?.metricId);
          const completionTracker = trackersMap[step.completionTracker?.trackerId];
          const completionMetric = completionTracker?.metrics?.find(m => m._id === step.completionTracker?.metricId);
          return {
            activation: {
              name: activationMetric?.name || '',
              value: activationMetric ? this.formatMetricValue(activationMetric, step.activationTracker.value) : '',
            },
            completion: {
              name: completionMetric?.name || '',
              value: completionMetric ? this.formatMetricValue(completionMetric, step.completionTracker.value) : '',
            },
          };
        })
      )
    );
  }

  formatMetricValue(metric: TrackerMetric, value: string): string {
    if (metric.type === TrackerMetricTypes.Boolean) {
      return value === '1' ? 'Yes' : 'No';
    }

    if (metric.type === TrackerMetricTypes.Duration) {
      return formatDuration(Number(value));
    }

    return value;
  }

  onBackClick() {
    const urlSegments = this.route.snapshot.url;
    const isRoutedEdit = urlSegments.length > 0 && urlSegments[urlSegments.length - 1]?.path === 'edit';
    if (isRoutedEdit) {
      this.router.navigate(['..'], {relativeTo: this.route});
    } else {
      this.exitEdit.emit();
    }
  }

  taskDrop(event: CdkDragDrop<TaskDropData>) {
    // If task is dropped in origin location
    if (
      event.previousIndex === event.currentIndex &&
      event.container.data.stepIndex === event.previousContainer.data.stepIndex
    ) {
      return;
    }

    if (event.previousContainer.data.isTemplate) {
      this.handleTemplateDrop(event);
      return;
    }

    if (event.previousContainer === event.container) {
      this.handleSameContainerDrop(event);
      return;
    }

    if (event.previousContainer !== event.container) {
      this.handleDifferentContainerDrop(event);
    }
  }

  handleTemplateDrop(event: CdkDragDrop<TaskDropData>) {
    this.project$
      .pipe(
        filter(project => !!project),
        take(1)
      )
      .subscribe(project => {
        const task = _.cloneDeep(event.previousContainer.data.taskTemplates[event.previousIndex]);
        task.users = this.projectsService.getProjectUsers(project);
        task.stepId = project.steps[event.container.data.stepIndex]?._id;
        event.container.data.tasks.splice(event.currentIndex, 0, task);
        this.loadingTaskIndex$.next(event.currentIndex);
        this.loadingStepIndex$.next(event.container.data.stepIndex);
        this.addProjectTask(task, event.currentIndex, project);
      });
  }

  addProjectTask(task: ProjectTask, currentIndex: number, project: Project) {
    this.store$.dispatch(
      new AddProjectTaskAction({
        projectId: project._id,
        stepId: task.stepId,
        task,
        taskIndex: currentIndex,
        onSuccess: () => this.onUpdateSuccess(),
        onFailure: err => this.onUpdateFailure(err),
      })
    );
  }

  handleSameContainerDrop(event: CdkDragDrop<TaskDropData>) {
    moveItemInArray(event.container.data.tasks, event.previousIndex, event.currentIndex);
    this.loadingTaskIndex$.next(event.currentIndex);
    this.loadingStepIndex$.next(event.container.data.stepIndex);
    this.project$
      .pipe(
        filter(project => !!project),
        take(1)
      )
      .subscribe(project => this.updateProjectAction(project));
  }

  updateProjectAction(project: Project) {
    this.store$.dispatch(
      new PutProjectAction({
        project,
        onSuccess: () => this.onUpdateSuccess(),
        onFailure: err => this.onUpdateFailure(err),
      })
    );
  }

  handleDifferentContainerDrop(event: CdkDragDrop<TaskDropData>) {
    const task = _.cloneDeep(event.previousContainer.data.tasks[event.previousIndex]);
    transferArrayItem(
      event.previousContainer.data.tasks,
      event.container.data.tasks,
      event.previousIndex,
      event.currentIndex
    );
    this.loadingTaskIndex$.next(event.currentIndex);
    this.loadingStepIndex$.next(event.container.data.stepIndex);

    this.project$
      .pipe(
        filter(project => !!project),
        take(1)
      )
      .subscribe(project => {
        // When a task is moved to a different step, the task is first deleted then, on success, added to the new step with a the new step id
        const newStepId = project.steps[event.container.data.stepIndex]?._id;
        if (!newStepId) {
          this.messageService.add('Cannot find step id');
          return;
        }
        task.stepId = newStepId;

        this.store$.dispatch(
          new MoveProjectTaskAction({
            projectId: project._id,
            stepId: task.stepId,
            task,
            taskIndex: event.currentIndex,
            onSuccess: () => this.onUpdateSuccess(),
            onFailure: err => this.onUpdateFailure(err),
          })
        );
      });
  }

  onUpdateSuccess() {
    this.loadingTaskIndex$.next(null);
    this.loadingStepIndex$.next(null);
  }

  onUpdateFailure(err: Error) {
    this.loadingTaskIndex$.next(null);
    this.loadingStepIndex$.next(null);
    this.messageService.add(err.message || 'There was a problem updating the project.');
  }

  onCreateTaskForProject(stepIndex: number) {
    combineLatest([this.project$, this.coinTypes$])
      .pipe(
        filter(([project, coinTypes]) => !!project && !!coinTypes),
        take(1)
      )
      .subscribe(([project, coinTypes]) => {
        const stepId = project.steps[stepIndex]?._id;
        const taskIndex = project.steps[stepIndex].tasks.length;
        if (!stepId || (!taskIndex && taskIndex !== 0)) {
          this.messageService.add('Cannot find step id or task index');
          return;
        }

        this.dialog.open(ProjectsTaskDialogComponent, {
          autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
          data: {
            project,
            coinTypes,
            stepId,
            taskIndex,
            newTask: true,
          },
          panelClass: 'task-dialog',
          disableClose: true,
        });
      });
  }

  onEditProjectTask(stepIndex: number, stepId: string, taskIndex: number) {
    // This is to prevent clicking on a task that is loading
    if (
      (stepIndex || stepIndex === 0) &&
      stepIndex === this.loadingStepIndex$.value &&
      (taskIndex || taskIndex === 0) &&
      taskIndex === this.loadingTaskIndex$.value
    ) {
      return;
    }

    combineLatest([this.project$, this.coinTypes$])
      .pipe(
        filter(([project, coinTypes]) => !!project && !!coinTypes),
        take(1)
      )
      .subscribe(([project, coinTypes]) => {
        this.dialog.open(ProjectsTaskDialogComponent, {
          autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
          data: {
            task: project.steps[stepIndex].tasks[taskIndex] || null,
            project,
            coinTypes,
            stepId,
            taskIndex,
            newTask: false,
          },
          panelClass: 'task-dialog',
          disableClose: true,
        });
      });
  }

  onTabChange(tab: string) {
    this.selectedTab$.next(tab);
  }

  openTaskTemplateDialog(taskTemplate?: TaskTemplate) {
    this.project$
      .pipe(
        filter(project => !!project),
        take(1)
      )
      .subscribe(project => {
        this.dialog.open(ProjectsTaskDialogComponent, {
          autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
          // this next line doesn't work. i need to specifify if new tempate to hide menu which allows deleting a not yet made template
          data: {taskTemplate, isTemplate: true, project},
          ...(taskTemplate ? EDIT_PROJECT_TASK_DIALOG_SETTINGS : NEW_PROJECT_TASK_DIALOG_SETTINGS),
        });
      });
  }

  onOpenStepDialog(step: ProjectStep, stepIndex: number): void {
    this.project$
      .pipe(
        filter(project => !!project),
        take(1)
      )
      .subscribe(project => {
        this.dialog.open(ProjectEditStepDialogComponent, {
          autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
          data: {step, stepIndex, project},
          width: '600px',
          minHeight: '610px',
          panelClass: 'project-edit-step-dialog',
          disableClose: true,
        });
      });
  }

  onStepEdit(step: ProjectStep, stepIndex: number) {
    this.onOpenStepDialog(step, stepIndex);
  }

  onOpenAlertsDialog(alert: ProjectAlert | undefined, stepId: string) {
    this.project$
      .pipe(
        filter(project => !!project),
        take(1)
      )
      .subscribe(project => {
        this.dialog.open(AlertsDialogComponent, {
          autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
          ...PROJECT_EDIT_ALERTS_DIALOG_CONFIG,
          data: {alert: alert || undefined, projectId: project._id, stepId},
        });
      });
  }

  onToggleAlert(event: Event, alert: ProjectAlert, index: number, stepId: string) {
    event.stopPropagation();
    this.loadingAlertIndex$.next({stepId, index});

    this.project$
      .pipe(
        filter(project => !!project),
        take(1)
      )
      .subscribe(project => {
        this.store$.dispatch(
          new UpdateAlertAction({
            alert: {...alert, active: !alert.active},
            stepId,
            projectId: project._id,
            onSuccess: () => this.onToggleSuccess(),
            onFailure: error => this.onToggleFailure(error),
          })
        );
      });
  }

  onToggleSuccess() {
    this.loadingAlertIndex$.next(null);
  }

  onToggleFailure(error: Error) {
    this.loadingAlertIndex$.next(null);
    this.messageService.add(error.message || 'Failed to toggle alert');
  }
}
